import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Download, Phone, Mail, Smartphone } from "lucide-react";
import { api } from "../../lib/api";

const PAGE_SIZE = 50;

export default function AppLeadsView() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/app-leads")
            .then((r) => setLeads(r.data || []))
            .catch(() => setLeads([]))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return leads;
        return leads.filter((l) =>
            [l.name, l.phone, l.email, l.source_page].filter(Boolean).some((v) => v.toLowerCase().includes(s))
        );
    }, [leads, search]);

    const visible = filtered.slice(0, PAGE_SIZE);

    const exportCsv = () => {
        const token = localStorage.getItem("at_token") || "";
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/app-leads/export.csv${search ? `?search=${encodeURIComponent(search)}` : ""}`;
        // open in new tab with Authorization via fetch + blob
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `app-leads-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
            });
    };

    return (
        <div data-testid="admin-app-leads">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Smartphone className="w-6 h-6 text-[#1E3A8A]" /> App Download Leads
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Phone numbers captured from the "Send me the download link" form on the Book My Tutor promo. {leads.length} total.</p>
                </div>
                <button
                    onClick={exportCsv}
                    disabled={leads.length === 0}
                    data-testid="app-leads-export"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-[#1E3A8A] text-sm font-semibold rounded-lg disabled:opacity-50"
                >
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, phone, email or source page…"
                    data-testid="app-leads-search"
                    className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
                    {leads.length === 0 ? "No app download leads yet. Once visitors submit the 'Send me the download link' form, they'll appear here." : "No leads match your search."}
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold">Captured</th>
                                    <th className="text-left px-4 py-3 font-semibold">Name</th>
                                    <th className="text-left px-4 py-3 font-semibold">Phone</th>
                                    <th className="text-left px-4 py-3 font-semibold">Email</th>
                                    <th className="text-left px-4 py-3 font-semibold">Source Page</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {visible.map((l) => (
                                    <tr key={l.id} data-testid={`app-lead-row-${l.id}`} className="hover:bg-slate-50/60">
                                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                                        <td className="px-4 py-3 font-medium text-[#0F172A]">{l.name || <span className="text-slate-400">—</span>}</td>
                                        <td className="px-4 py-3">
                                            <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline font-medium">
                                                <Phone className="w-3.5 h-3.5" /> {l.phone}
                                            </a>
                                        </td>
                                        <td className="px-4 py-3">
                                            {l.email ? (
                                                <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-[#1E3A8A] hover:underline">
                                                    <Mail className="w-3.5 h-3.5" /> {l.email}
                                                </a>
                                            ) : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <code className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{l.source_page || "—"}</code>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filtered.length > PAGE_SIZE && (
                        <div className="text-xs text-slate-500 text-center py-3 border-t border-slate-100">
                            Showing first {PAGE_SIZE} of {filtered.length} leads — refine search or export CSV for the full list.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
